import { Box, Card, Divider, Typography } from '@mui/material';
import type { ReactNode } from 'react';

interface DetailCardProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}

/**
 * Sectioned surface used across every detail screen for clear separation.
 *
 * **`alignSelf: 'stretch'`, and neither `height` nor `minHeight`.** The rule
 * exists for one thing — cards side by side in a row ending level — and both of
 * the obvious spellings get it wrong in a flex COLUMN, where every detail page
 * puts these:
 *
 *  - `height: '100%'` resolves against the container and becomes a CAP. `Card`
 *    clips, so a longer body was cut off with nothing to scroll: measured at
 *    2580px of content in a 1746px card on an incident with twenty policy
 *    evaluations. Reported from the page as "not able to scroll".
 *  - `minHeight: '100%'` fixes the clipping and then forces EVERY card to the
 *    full container height — five cards each 1144px tall with one screen of
 *    content between them. Reported from the page as "lot of gaps".
 *
 * `alignSelf` stretches on the CROSS axis, so in a row or a grid it does the
 * equalising this was always for, and in a column it touches width and leaves
 * height to the content. Measured both ways: 249/202/129/177/119 down an
 * incident page, and still level across four rows of the dashboard.
 *
 * The cap went unnoticed for as long as it did because every long body used to
 * be internally scrollable — a `JsonViewer` with its own `maxHeight`. Replacing
 * those payloads with prose removed the inner scroller and exposed it.
 */
export function DetailCard({ title, subtitle, action, icon, children, dense }: DetailCardProps) {
  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25 }}>
        {icon && <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <Divider />
      <Box sx={{ p: dense ? 0 : 2, flex: 1 }}>{children}</Box>
    </Card>
  );
}
